/**
 * @fileoverview Componente responsável por filtrar resultados por usuários.
 */

import { Box, Chip, Popper, TextField, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { UserAvatar } from "../LeadShared";
import { measureDialogSide } from "../../utils/dialogSidePlacement";

const useStyles = makeStyles(() => ({
  option: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    width: "100%",
  },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

// Avatares (base64) só são baixados quando o dropdown abre pela primeira vez, e
// ficam em cache por alguns minutos: o modal de Filtros desmonta este componente
// ao fechar, e sem cache cada abertura baixaria todas as fotos de novo.
const AVATAR_CACHE_TTL_MS = 5 * 60 * 1000;
let avatarCache = { key: null, at: 0, byId: {} };

async function loadAvatarMap() {
  const key = localStorage.getItem("companyId") || "";
  if (avatarCache.key === key && Date.now() - avatarCache.at < AVATAR_CACHE_TTL_MS) {
    return avatarCache.byId;
  }
  const { data } = await api.get(`/users/list`, { params: { withProfileImage: true } });
  const byId = {};
  (data || []).forEach((u) => { byId[u.id] = u.profileImage || null; });
  avatarCache = { key, at: Date.now(), byId };
  return byId;
}

export function UsersFilter({
  onFiltered,
  initialUsers,
  autocompleteClasses,
  textFieldClassName,
  placeholderText,
  containerStyle,
  placement,
}) {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [selecteds, setSelecteds] = useState([]);
  const rootRef = useRef(null);
  // Offset para abrir ao lado do modal (desktop), medido ao abrir; null = abaixo.
  const [sideOffset, setSideOffset] = useState(null);
  const avatarsLoadedRef = useRef(false);

  useEffect(() => {
    async function fetchData() {
      await loadUsers();
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (
      Array.isArray(initialUsers) &&
      Array.isArray(users) &&
      users.length > 0
    ) {
      const selectedUsers = users.filter(user => initialUsers.includes(user.id));
      setSelecteds(selectedUsers);
    } else if (initialUsers === undefined || initialUsers.length === 0) {
      setSelecteds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUsers, users]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get(`/users/list`);
      setUsers(data.map((u) => ({ id: u.id, name: u.name })));
    } catch (err) {
      toastError(err);
    }
  };

  const onChange = async (value) => {
    setSelecteds(value);
    onFiltered(value);
  };

  const handleOpen = async () => {
    const root = rootRef.current;
    // measureDialogSide devolve o offset a partir da borda ESQUERDA do campo (o
    // formato do anchorOrigin do MUI); o `offset` do popper.js em "right-start"
    // conta a partir da borda DIREITA — por isso subtraímos a largura do campo.
    const side = placement === "right" && root ? measureDialogSide(root, root.offsetWidth) : null;
    setSideOffset(side == null ? null : side - root.offsetWidth);
    if (avatarsLoadedRef.current) return;
    avatarsLoadedRef.current = true;
    try {
      const byId = await loadAvatarMap();
      setUsers((prev) => prev.map((u) => ({ ...u, profileImage: byId[u.id] || null })));
    } catch (err) {
      avatarsLoadedRef.current = false; // sem foto, ficam as iniciais; tenta de novo na próxima
    }
  };

  // Popper à direita do modal: componente memoizado por offset para o Popper do
  // MUI não recriar a instância do popper.js a cada tecla (ele observa `modifiers`).
  const SidePopper = useMemo(() => {
    if (sideOffset == null) return undefined;
    const modifiers = {
      offset: { enabled: true, offset: `0, ${sideOffset}` },
      preventOverflow: { enabled: true, boundariesElement: "viewport" },
      flip: { enabled: false },
    };
    return function SidePopperComponent(props) {
      return <Popper {...props} placement="right-start" modifiers={modifiers} />;
    };
  }, [sideOffset]);

  return (
    <Box style={containerStyle || { padding: "0px 10px 10px" }} ref={rootRef}>
      <Autocomplete
        multiple
        onOpen={handleOpen}
        size="small"
        classes={autocompleteClasses}
        options={users}
        value={selecteds}
        onChange={(e, v, r) => onChange(v)}
        getOptionLabel={(option) => option.name}
        renderOption={(option) => (
          <div className={classes.option}>
            <UserAvatar user={option} size={26} />
            <span className={classes.name}>{option.name}</span>
          </div>
        )}
        PopperComponent={SidePopper}
        getOptionSelected={(option, value) => {
          return (
            option?.id === value?.id ||
            option?.name.toLowerCase() === value?.name.toLowerCase()
          );
        }}
        renderTags={(value, getUserProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              style={{
                backgroundColor: "#bfbfbf",
                textShadow: "1px 1px 1px #000",
                color: "white",
              }}
              label={option.name}
              {...getUserProps({ index })}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            className={textFieldClassName}
            variant="outlined"
            placeholder={placeholderText || i18n.t("tickets.filters.user")}
          />
        )}
      />
    </Box>
  );
}
